import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import queueService from '../../services/queueService';
import { announcementText, groupByStage, newCalls } from '../../utils/queueLogic';
import './QueueDisplayPage.css';

const POLL_MS = 4000;
const MAX_WAITING_SHOWN = 8;

/** Speaks one announcement after another; the browser needs one click on "Sound on" before it will speak. */
const speak = (text) => {
  try {
    if (!window.speechSynthesis || typeof window.SpeechSynthesisUtterance === 'undefined') return;
    const u = new window.SpeechSynthesisUtterance(text);
    u.rate = 0.9;
    window.speechSynthesis.speak(u);
  } catch (e) { /* no speech engine: the screen alone still works */ }
};

/**
 * Full-screen board for a TV in the waiting room. It shows token labels and counter names only; the server never sends a
 * patient name or number to this screen. Log in on the TV with a Front Office account and keep this page open.
 */
export const QueueDisplayPage = () => {
  const [board, setBoard] = useState(null);
  const [error, setError] = useState('');
  const [sound, setSound] = useState(false);
  const [now, setNow] = useState(new Date());
  const previous = useRef(null);
  const soundRef = useRef(false);
  soundRef.current = sound;

  const load = useCallback(async () => {
    try {
      const res = await queueService.board();
      if (!res.success) { setError(res.message || 'Could not load the queue.'); return; }
      setError('');
      const counters = res.data?.counters || [];
      if (soundRef.current) newCalls(previous.current, counters).forEach((c) => speak(announcementText(c.tokenLabel, c.counterName)));
      previous.current = counters;
      setBoard(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Connection lost. Trying again…');
    }
  }, []);

  useEffect(() => { load(); const id = setInterval(load, POLL_MS); return () => clearInterval(id); }, [load]);
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 15000); return () => clearInterval(id); }, []);
  useEffect(() => () => { try { window.speechSynthesis?.cancel(); } catch (e) { /* nothing to stop */ } }, []);

  const groups = groupByStage(board?.counters);
  const waitingOf = (stage) => (board?.waiting || []).filter((w) => w.stage === stage);

  return (
    <div className="qd-screen" role="main">
      <header className="qd-top">
        <h1>Please wait for your token</h1>
        <div className="qd-top-right">
          <span className="qd-clock">{now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          <button type="button" className="qd-btn" onClick={() => { setSound((s) => !s); if (!sound) speak('Sound is on'); }} aria-pressed={sound}>
            {sound ? 'Sound on' : 'Sound off'}
          </button>
          <Link to="/queue" className="qd-btn">Exit</Link>
        </div>
      </header>

      {error && <div className="qd-error" role="alert">{error}</div>}
      {!board && !error && <p className="qd-empty">Loading…</p>}
      {board && groups.length === 0 && <p className="qd-empty">No counters are set up for this centre.</p>}

      <div className="qd-stages">
        {groups.map((g) => (
          <section key={g.code} className="qd-stage" aria-label={g.label}>
            <h2>{g.label}</h2>
            <div className="qd-counters">
              {g.counters.map((c) => (
                <div key={c.counterId} className={`qd-counter ${c.tokenLabel ? 'busy' : ''}`}>
                  <div className="qd-counter-name">{c.counterName}</div>
                  <div className="qd-token" aria-live="polite">{c.tokenLabel || '—'}</div>
                </div>
              ))}
            </div>
            <div className="qd-next">
              <span>Next:</span>
              {waitingOf(g.code).length === 0 ? <em>no one waiting</em> : waitingOf(g.code).slice(0, MAX_WAITING_SHOWN).map((w) => (
                <span key={w.tokenLabel} className={`qd-chip ${w.priority ? 'priority' : ''}`}>{w.tokenLabel}</span>
              ))}
              {waitingOf(g.code).length > MAX_WAITING_SHOWN && <span className="qd-more">+{waitingOf(g.code).length - MAX_WAITING_SHOWN} more</span>}
            </div>
          </section>
        ))}
      </div>
      <footer className="qd-foot">{board ? `Updated ${new Date(board.serverTime).toLocaleTimeString()}` : ''}</footer>
    </div>
  );
};

export default QueueDisplayPage;
